<template>
  <b-modal
    ref="refModal"
    id="company-account-statement-modal"
    title="Account Statement"
    no-close-on-backdrop
  >
    <validation-observer ref="companyAccountStatementForm">
      <b-form id="company-account-statement">
        <validation-provider
          #default="{ errors }"
          name="Date From"
          rules="required"
        >
          <b-form-group
            label="Date From"
            label-for="date-to"
          >
            <flat-pickr
              v-model="dateFrom"
              class="form-control"
              :config="{ dateFormat: 'd/m/Y', enableTime: false }"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          name="Date To"
          rules="required"
        >
          <b-form-group
            label="Date To"
            label-for="date-to"
          >
            <flat-pickr
              v-model="dateTo"
              class="form-control"
              :config="{ dateFormat: 'd/m/Y', enableTime: false }"
            />
            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          name="File Format"
          rules="required"
        >
          <b-form-group
            label="File Format"
            label-for="file-format"
          >
            <v-select
              v-model="fileFormat"
              :options="fileFormatOptions"
              label="File Format"
              :reduce="(format) => format.value"
              input-id="file-format"
            >
              <template #option="data">
                <span> {{ data.name }}</span>
              </template>

              <template #selected-option="data">
                <span> {{ data.name }}</span>
              </template>
            </v-select>

            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <validation-provider
          #default="{ errors }"
          name="Transactions Status"
          rules="required"
        >
          <b-form-group
            label="Transaction Status"
            label-for="transaction-status"
          >
            <v-select
              v-model="transactionStatusIds"
              :options="transactionStatusList"
              label="name"
              :reduce="(transaction) => transaction.id"
              input-id="transaction-status"
              multiple
            >
              <template #option="data">
                <div
                  :class="`bg-${resolveTransactionStatusVariantAndIcon(data)}`"
                  class="rounded-circle d-inline-block mr-50"
                  style="height: 10px; width: 10px"
                />
                <span> {{ data.name }}</span>
              </template>

              <template #selected-option="data">
                <div
                  :class="`bg-${resolveTransactionStatusVariantAndIcon(data)}`"
                  class="rounded-circle d-inline-block mr-50"
                  style="height: 10px; width: 10px"
                />
                <span> {{ data.name }}</span>
              </template>
            </v-select>

            <small class="text-danger">{{ errors[0] }}</small>
          </b-form-group>
        </validation-provider>

        <b-form-group
          label="Emails"
          label-for="company-email"
        >
          <v-select
            v-model="companyEmaiSelected"
            :options="companyEmailList"
            input-id="transaction-status"
            multiple
          >
            <template #option="data">
              <span> {{ data.label }}</span>
            </template>

            <template #selected-option="data">
              <span> {{ data.label }}</span>
            </template>
          </v-select>
        </b-form-group>
      </b-form>
    </validation-observer>

    <template #modal-footer="{ ok, cancel, hide }">
      <b-button
        variant="outline-secondary"
        @click="
          resetFormData();
          cancel();
        "
      >
        Cancel
      </b-button>

      <b-button
        variant="outline-primary"
        @click="validateFormToDownload"
      >
        Download
      </b-button>

      <b-button
        variant="primary"
        @click="validateFormToSend"
      >
        Send
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { BButton, BForm, BFormGroup, BFormFile } from 'bootstrap-vue';
import { ref } from '@vue/composition-api';
import { required } from '@validations';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import flatPickr from 'vue-flatpickr-component';
import router from '@/router';
import useCompanyTransaction from '@/modules/company/composables/useCompanyTransaction';
import vSelect from 'vue-select';
import useUtils from '@/composables/useUtils';
import { FileFormat } from '@/modules/company/FileFormat';

export default {
  props: {
    companyEmails: {
      type: String,
    },
  },
  setup(props) {
    const { convertDateToUTC } = useUtils();
    const {
      downloadAccountStatement,
      resolveTransactionStatusVariantAndIcon,
      fetchTransactionStatus,
      sendAccountStatement,
    } = useCompanyTransaction();

    const dateFrom = ref(new Date());
    const dateTo = ref(new Date());
    const transactionStatusIds = ref([]);
    const transactionStatusList = ref([]);
    const companyEmailList = ref([]);
    const companyEmaiSelected = ref([]);
    const refModal = ref(null);
    const companyAccountStatementForm = ref(null);
    const fileFormat = ref(null);
    const fileFormatOptions = [
      { name: 'PDF', value: FileFormat.PDF },
      { name: 'CSV', value: FileFormat.CSV },
    ];

    const resetFormData = () => {
      dateFrom.value = new Date();
      dateTo.value = new Date();
      transactionStatusIds.value = [];
      companyEmaiSelected.value = [];
      fileFormat, (value = null);
      companyAccountStatementForm.value.reset();
    };

    const initData = async () => {
      const transactionStatus = await fetchTransactionStatus();
      transactionStatusList.value = transactionStatus;

      if (props.companyEmails)
        companyEmailList.value = props.companyEmails.split(', ');
    };

    const validateFormToSend = (modalEvt) => {
      modalEvt.preventDefault();

      if (companyEmaiSelected.length === 0) return;

      companyAccountStatementForm.value.validate().then(async (valid) => {
        if (!valid) return;
        const params = {
          dateFrom: convertDateToUTC(dateFrom.value),
          dateTo: convertDateToUTC(dateTo.value),
          transactionStatusIds: transactionStatusIds.value,
          emailList: companyEmaiSelected.value,
        };
        const response = await sendAccountStatement(
          router.currentRoute.params.id,
          params
        );
        if (response !== null) refModal.value.hide();

        resetFormData();
      });
    };

    const validateFormToDownload = (modalEvt) => {
      modalEvt.preventDefault();

      companyAccountStatementForm.value.validate().then(async (valid) => {
        if (!valid) return;

        const params = {
          dateFrom: convertDateToUTC(dateFrom.value),
          dateTo: convertDateToUTC(dateTo.value),
          transactionStatusIds: transactionStatusIds.value,
          fileFormat: fileFormat.value,
        };
        const response = await downloadAccountStatement(
          router.currentRoute.params.id,
          params
        );

        if (response !== null) {
          // Extract filename from Content-Disposition header
          const disposition = response.headers['content-disposition'];
          let filename = 'account-statement.'; // fallback

          if (disposition && disposition.includes('filename=')) {
            filename = disposition
              .split('filename=')[1]
              .split(';')[0]
              .replace(/['"]/g, '');
          }

          // Create a blob URL and trigger the download
          const blob = new Blob([response.data], {
            type: response.data.type || 'text/',
          });
          const url = URL.createObjectURL(blob);

          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();

          // Cleanup
          document.body.removeChild(link);
          setTimeout(() => URL.revokeObjectURL(url), 5000);
          refModal.value.hide();
        }

        resetFormData();
      });
    };

    initData();
    return {
      // Reactive
      dateFrom,
      dateTo,
      transactionStatusIds,
      refModal,
      transactionStatusIds,
      transactionStatusList,
      companyEmailList,
      companyEmaiSelected,
      companyAccountStatementForm,
      fileFormat,
      fileFormatOptions,

      // Method
      resolveTransactionStatusVariantAndIcon,
      validateFormToSend,
      validateFormToDownload,
      resetFormData,

      // Validator
      required,
    };
  },
  components: {
    BButton,
    BForm,
    BFormGroup,
    BFormFile,
    flatPickr,
    ValidationObserver,
    ValidationProvider,
    vSelect,
  },
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
@import '@core/scss/vue/libs/vue-flatpicker.scss';

#company-account-statement {
  .vs__dropdown-menu {
    max-height: 200px !important;
  }
}
</style>
